import chevronDown from "@assets/images/ChevronDown.svg";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@saleor/components/Checkbox";
import useElementScroll, {
  isScrolledToBottom
} from "@saleor/hooks/useElementScroll";
import { FetchMoreProps } from "@saleor/types";
import classNames from "classnames";
import { GetItemPropsOptions } from "downshift";
import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import Hr from "../Hr";

const menuItemHeight = 46;
const maxMenuItems = 5;
const offset = 24;

export interface MultiChipsActionType {
  label: string;
  onClick: () => void;
}
export interface MultiChipsChoiceType {
  label: string;
  value: any;
  disabled?: boolean;
}
export interface MultiAutocompleteChipFieldContentProps
  extends Partial<FetchMoreProps> {
  add?: MultiChipsActionType;
  manualAdd?: () => void;
  choices: MultiChipsChoiceType[];
  selectedValues: string[];
  getItemProps: (options: GetItemPropsOptions) => any;
  highlightedIndex: number;
  inputValue: string;
  limit: number;
  hasSelectAll: boolean;
  selectedAll: boolean;
  selectAllProps: MultiChipsChoiceType;
}

const useStyles = makeStyles(
  theme => ({
    add: {
      background: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "100%",
      height: 24,
      margin: theme.spacing(),
      width: 24
    },
    addIcon: {
      height: 24,
      margin: 9,
      width: 20
    },
    arrowContainer: {
      position: "relative"
    },
    arrowInnerContainer: {
      alignItems: "center",
      background:
        theme.palette.type === "light"
          ? theme.palette.grey[50]
          : theme.palette.grey[900],
      bottom: 0,
      display: "flex",
      height: 30,
      justifyContent: "center",
      opacity: 1,
      position: "absolute",
      transition: theme.transitions.duration.short + "ms",
      width: "100%"
    },
    checkbox: {
      height: 24,
      width: 20
    },
    content: {
      maxHeight: menuItemHeight * maxMenuItems + theme.spacing(2),
      overflowY: "scroll",
      padding: 8
    },
    hide: {
      opacity: 0,
      zIndex: -1
    },
    hr: {
      margin: theme.spacing(1, 0)
    },
    menuItem: {
      "&:focus": {
        backgroundColor: [
          theme.palette.background.default,
          "!important"
        ] as any,
        color: theme.palette.primary.main,
        fontWeight: 400
      },
      "&:hover": {
        backgroundColor: [
          theme.palette.background.default,
          "!important"
        ] as any,
        color: theme.palette.primary.main,
        fontWeight: 700
      },
      borderRadius: 4,
      display: "grid",
      gridColumnGap: theme.spacing(1),
      gridTemplateColumns: "30px 1fr",
      height: "auto",
      marginBottom: theme.spacing(0.5),
      padding: 0,
      whiteSpace: "normal"
    },
    menuItemLabel: {
      overflowWrap: "break-word"
    },
    progress: {},
    progressContainer: {
      display: "flex",
      justifyContent: "center"
    },
    root: {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
      left: 0,
      marginTop: theme.spacing(),
      overflow: "hidden",
      position: "absolute",
      right: 0,
      zIndex: 22
    }
  }),
  {
    name: "MultiAutocompleteChipFieldContent"
  }
);

function getChoiceIndex(
  index: number,
  selectedValues: string[],
  hasSelectAll: boolean
) {
  let choiceIndex = index;
  if (hasSelectAll) {
    choiceIndex += 2;
  }
  if (selectedValues.length > 0) {
    choiceIndex += 1 + selectedValues.length;
  }

  return choiceIndex;
}

const MultiAutocompleteChipFieldContent: React.FC<MultiAutocompleteChipFieldContentProps> = props => {
  const {
    choices,
    selectedValues,
    getItemProps,
    hasMore,
    highlightedIndex,
    loading,
    onFetchMore,
    limit,
    selectedAll,
    selectAllProps,
    hasSelectAll
  } = props;
  const classes = useStyles(props);
  const anchor = React.useRef<HTMLDivElement>();
  const scrollPosition = useElementScroll(anchor);
  const [calledForMore, setCalledForMore] = useState(false);

  const scrolledToBottom = isScrolledToBottom(anchor, scrollPosition, offset);

  useEffect(() => {
    if (!calledForMore && onFetchMore && scrolledToBottom) {
      onFetchMore();
      setCalledForMore(true);
    }
  }, [scrolledToBottom]);

  useEffect(() => {
    if (calledForMore && !loading) {
      setCalledForMore(false);
    }
  }, [loading]);

  return (
    <Paper className={classes.root}>
      <div className={classes.content} ref={anchor}>
        {choices.length > 0 || selectedValues.length > 0 ? (
          <>
            {hasSelectAll ? (
              <>
                <MenuItem
                  className={classes.menuItem}
                  key={selectAllProps.value}
                  selected={highlightedIndex === 0}
                  disabled={
                    selectAllProps.disabled || selectedValues.length >= limit
                  }
                  component="div"
                  {...((getItemProps({
                    index: 0,
                    item: selectAllProps.value
                  }) as unknown) as React.HTMLAttributes<HTMLDivElement>)}
                  data-test="multichips-select-option"
                >
                  <Checkbox
                    checked={selectedAll}
                    disabled={
                      selectAllProps.disabled || selectedValues.length >= limit
                    }
                    className={classes.checkbox}
                    disableRipple
                  />
                  <span className={classes.menuItemLabel}>
                    {selectAllProps.label}
                  </span>
                </MenuItem>
                <Hr className={classes.hr} />
              </>
            ) : null}
            {choices.map((suggestion, index) => {
              const choiceIndex = getChoiceIndex(
                index,
                selectedValues,
                hasSelectAll
              );

              return (
                <MenuItem
                  className={classes.menuItem}
                  key={suggestion.value}
                  selected={highlightedIndex === choiceIndex}
                  disabled={
                    suggestion.disabled || selectedValues.length >= limit
                  }
                  component="div"
                  {...((getItemProps({
                    index: choiceIndex,
                    item: suggestion.value
                  }) as unknown) as React.HTMLAttributes<HTMLDivElement>)}
                  data-test="multichips-select-option"
                >
                  <Checkbox
                    checked={
                      selectedValues.includes(suggestion.value) || selectedAll
                    }
                    disabled={
                      suggestion.disabled || selectedValues.length >= limit
                    }
                    className={classes.checkbox}
                    disableRipple
                  />
                  <span className={classes.menuItemLabel}>
                    {suggestion.label}
                  </span>
                </MenuItem>
              );
            })}
            {hasMore && (
              <>
                <Hr className={classes.hr} />
                <div className={classes.progressContainer}>
                  <CircularProgress className={classes.progress} size={24} />
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <MenuItem
              disabled={true}
              component="div"
              data-test="multichips-select-no-options"
            >
              <FormattedMessage defaultMessage="No results found" />
            </MenuItem>
          </>
        )}
      </div>
      {choices.length > maxMenuItems && (
        <div className={classes.arrowContainer}>
          <div
            className={classNames(classes.arrowInnerContainer, {
              // Needs to be explicitely compared to false because
              // scrolledToBottom can be either true, false or undefined
              [classes.hide]: scrolledToBottom !== false
            })}
          >
            <SVG src={chevronDown} />
          </div>
        </div>
      )}
    </Paper>
  );
};

MultiAutocompleteChipFieldContent.displayName =
  "MultiAutocompleteChipFieldContent";
export default MultiAutocompleteChipFieldContent;
